import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import ServiceHistoryCard from './components/ServiceHistoryCard';
import DocumentCard from './components/DocumentCard';
import WarrantyCard from './components/WarrantyCard';
import AccountSettingsForm from './components/AccountSettingsForm';
import PaymentMethodCard from './components/PaymentMethodCard';
import CommunicationHistoryItem from './components/CommunicationHistoryItem';
import QuickActionCard from './components/QuickActionCard';
import NotificationPreferences from './components/NotificationPreferences';


const CustomerAccountPortal = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const mockUserData = {
    firstName: "Marie",
    lastName: "Dubois",
    email: "marie.dubois@email.fr",
    phone: "+33 6 12 34 56 78",
    address: "45 Avenue des Champs-Élysées",
    postalCode: "75008",
    city: "Paris",
    emailNotifications: true,
    smsNotifications: true,
    autoReminders: true,
    promotionalEmails: false
  };

  const mockServiceHistory = [
  {
    id: 1,
    reference: "INT-2025-001234",
    title: "Réparation fuite robinet cuisine",
    status: "completed",
    date: "2025-11-15T10:00:00",
    completionDate: "2025-11-15T11:30:00",
    address: "45 Avenue des Champs-Élysées, 75008 Paris",
    technician: "Jean Martin",
    price: "89,00 €",
    icon: "Droplet",
    equipment: "Robinet mitigeur",
    problem: "Fuite au niveau du joint",
    duration: "1h 30min",
    warranty: true,
    notes: "Remplacement du joint d'étanchéité et vérification de la pression. Aucun autre problème détecté.",
    photos: [
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec3e9d47-1764879181671.png",
      alt: "Close-up view of kitchen faucet showing water leak at base joint with visible water droplets",
      label: "Avant réparation"
    },
    {
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec3e9d47-1764879181671.png",
      alt: "Repaired kitchen faucet with new seal installed showing clean chrome finish without leaks",
      label: "Après réparation"
    }]

  },
  {
    id: 2,
    reference: "INT-2025-001189",
    title: "Installation chauffe-eau électrique",
    status: "completed",
    date: "2025-10-28T14:00:00",
    completionDate: "2025-10-28T17:00:00",
    address: "45 Avenue des Champs-Élysées, 75008 Paris",
    technician: "Pierre Lefebvre",
    price: "450,00 €",
    icon: "Flame",
    equipment: "Chauffe-eau 200L",
    problem: "Installation neuve",
    duration: "3h 00min",
    warranty: true,
    notes: "Installation complète avec raccordement électrique et plomberie. Test de fonctionnement effectué.",
    photos: [
    {
      url: "https://images.unsplash.com/photo-1622331589387-f78a7ced8dff",
      alt: "New white 200L electric water heater mounted on wall with copper pipes and electrical connections",
      label: "Installation terminée"
    }]

  },
  {
    id: 3,
    reference: "INT-2025-001456",
    title: "Débouchage canalisation salle de bain",
    status: "scheduled",
    date: "2025-12-05T09:00:00",
    address: "45 Avenue des Champs-Élysées, 75008 Paris",
    technician: "Thomas Rousseau",
    price: "120,00 €",
    icon: "Waves",
    equipment: "Canalisation principale",
    problem: "Évacuation lente",
    duration: "2h 00min estimé",
    warranty: false
  }];


  const mockDocuments = [
  {
    id: 1,
    name: "Facture INT-2025-001234",
    type: "invoice",
    date: "2025-11-15",
    size: 245678,
    format: "PDF"
  },
  {
    id: 2,
    name: "Certificat de garantie - Robinet",
    type: "warranty",
    date: "2025-11-15",
    size: 156789,
    format: "PDF"
  },
  {
    id: 3,
    name: "Rapport d\'intervention - Chauffe-eau",
    type: "report",
    date: "2025-10-28",
    size: 389012,
    format: "PDF"
  },
  {
    id: 4,
    name: "Facture INT-2025-001189",
    type: "invoice",
    date: "2025-10-28",
    size: 267890,
    format: "PDF"
  }];


  const mockWarranties = [
  {
    id: 1,
    reference: "INT-2025-001234",
    serviceName: "Réparation fuite robinet cuisine",
    serviceDate: "2025-11-15",
    expirationDate: "2026-05-15",
    coverage: [
    "Pièces de rechange fournies",
    "Main d\'œuvre pour réparation",
    "Déplacement du technicien"]

  },
  {
    id: 2,
    reference: "INT-2025-001189",
    serviceName: "Installation chauffe-eau électrique",
    serviceDate: "2025-10-28",
    expirationDate: "2026-04-28",
    coverage: [
    "Chauffe-eau et composants",
    "Installation et raccordements",
    "Vérification annuelle gratuite"]

  }];


  const mockPaymentMethods = [
  {
    id: 1,
    type: "visa",
    last4: "4242",
    expiryDate: "12/26",
    isDefault: true
  },
  {
    id: 2,
    type: "sepa",
    iban: "FR76 **** **** **** **89",
    isDefault: false
  }];


  const mockCommunications = [
  {
    id: 1,
    type: "complaint",
    subject: "Retard intervention du 15/11",
    message: "Le technicien est arrivé avec 30 minutes de retard. Demande de geste commercial.",
    status: "resolved",
    date: "2025-11-15T12:00:00",
    agent: "Sophie Martin",
    reference: "REC-2025-0045"
  },
  {
    id: 2,
    type: "email",
    subject: "Confirmation de rendez-vous",
    message: "Votre intervention est confirmée pour le 05/12/2025 à 09h00. Le technicien Thomas Rousseau vous contactera 30 minutes avant son arrivée.",
    status: "closed",
    date: "2025-11-28T16:30:00",
    reference: "INT-2025-001456"
  },
  {
    id: 3,
    type: "feedback",
    subject: "Satisfaction intervention du 28/10",
    message: "Merci pour votre retour positif concernant l'installation du chauffe-eau. Nous sommes ravis que vous soyez satisfait.",
    status: "closed",
    date: "2025-10-29T10:15:00",
    agent: "Marie Leroy"
  }];


  const sidebarSections = [
  { id: 'dashboard', label: 'Tableau de bord', icon: 'LayoutDashboard' },
  { id: 'history', label: 'Historique des services', icon: 'History' },
  { id: 'documents', label: 'Mes documents', icon: 'FileText' },
  { id: 'warranties', label: 'Garanties actives', icon: 'Shield' },
  { id: 'communications', label: 'Communications', icon: 'MessageSquare' },
  { id: 'payments', label: 'Moyens de paiement', icon: 'CreditCard' },
  { id: 'settings', label: 'Paramètres du compte', icon: 'Settings' }];


  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    navigate('/login-authentication');
  };

  const handleDownloadInvoice = (serviceId) => {
    console.log('Downloading invoice for service:', serviceId);
  };

  const handleCreateSAV = (serviceId) => {
    console.log('Creating SAV ticket for service:', serviceId);
  };

  const handleViewDetails = (id) => {
    console.log('Viewing details for:', id);
  };

  const handleDownloadDocument = (documentId) => {
    console.log('Downloading document:', documentId);
  };

  const handleShareDocument = (documentId) => {
    console.log('Sharing document:', documentId);
  };

  const handleViewDocument = (documentId) => {
    console.log('Viewing document:', documentId);
  };

  const handleSaveSettings = async (data) => {
    console.log('Saving settings:', data);
    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  const handleSetDefaultPayment = (methodId) => {
    console.log('Setting default payment method:', methodId);
  };

  const handleRemovePayment = (methodId) => {
    console.log('Removing payment method:', methodId);
  };

  const handleEditPayment = (methodId) => {
    console.log('Editing payment method:', methodId);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Bienvenue, {mockUserData?.firstName}
              </h1>
              <p className="text-muted-foreground">
                Gérez vos services, documents et paramètres de compte
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <QuickActionCard
                title="Réserver un nouveau service"
                description="Planifiez une intervention en quelques clics"
                icon="Calendar"
                iconColor="bg-primary"
                buttonText="Réserver maintenant"
                onClick={() => navigate('/service-booking-wizard')} />

              <QuickActionCard
                title="Suivre une intervention"
                description="Consultez le statut de vos demandes en cours"
                icon="MapPin"
                iconColor="bg-success"
                buttonText="Voir le suivi"
                onClick={() => navigate('/public-service-tracking')} />

            </div>
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-foreground">Services récents</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => setActiveSection('history')}>

                  Voir tout
                </Button>
              </div>
              <div className="space-y-4">
                {mockServiceHistory?.slice(0, 2)?.map((service) =>
                <ServiceHistoryCard
                  key={service?.id}
                  service={service}
                  onViewDetails={handleViewDetails}
                  onDownloadInvoice={handleDownloadInvoice}
                  onCreateSAV={handleCreateSAV} />

                )}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-foreground">Garanties actives</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => setActiveSection('warranties')}>

                  Voir tout
                </Button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {mockWarranties?.slice(0, 2)?.map((warranty) =>
                <WarrantyCard
                  key={warranty?.id}
                  warranty={warranty}
                  onCreateSAV={handleCreateSAV} />

                )}
              </div>
            </div>
          </div>);


      case 'history':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Historique des services
              </h1>
              <p className="text-muted-foreground">
                Consultez tous vos services passés et à venir
              </p>
            </div>
            <div className="space-y-4">
              {mockServiceHistory?.map((service) =>
              <ServiceHistoryCard
                key={service?.id}
                service={service}
                onViewDetails={handleViewDetails}
                onDownloadInvoice={handleDownloadInvoice}
                onCreateSAV={handleCreateSAV} />

              )}
            </div>
          </div>);


      case 'documents':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Mes documents
              </h1>
              <p className="text-muted-foreground">
                Accédez à vos factures, garanties et rapports d'intervention
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {mockDocuments?.map((document) =>
              <DocumentCard
                key={document?.id}
                document={document}
                onDownload={handleDownloadDocument}
                onShare={handleShareDocument}
                onView={handleViewDocument} />

              )}
            </div>
          </div>);


      case 'warranties':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Garanties actives
              </h1>
              <p className="text-muted-foreground">
                Suivez vos garanties et créez des tickets SAV si nécessaire
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {mockWarranties?.map((warranty) =>
              <WarrantyCard
                key={warranty?.id}
                warranty={warranty}
                onCreateSAV={handleCreateSAV} />

              )}
            </div>
          </div>);


      case 'communications':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Historique des communications
              </h1>
              <p className="text-muted-foreground">
                Consultez tous vos échanges avec notre service client
              </p>
            </div>
            <div className="space-y-4">
              {mockCommunications?.map((communication) =>
              <CommunicationHistoryItem
                key={communication?.id}
                communication={communication}
                onViewDetails={handleViewDetails} />

              )}
            </div>
          </div>);


      case 'payments':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Moyens de paiement
              </h1>
              <p className="text-muted-foreground">
                Gérez vos cartes bancaires et prélèvements SEPA
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {mockPaymentMethods?.map((method) =>
              <PaymentMethodCard
                key={method?.id}
                method={method}
                isDefault={method?.isDefault}
                onSetDefault={handleSetDefaultPayment}
                onRemove={handleRemovePayment}
                onEdit={handleEditPayment} />

              )}
            </div>
            <Button
              variant="outline"
              iconName="Plus"
              iconPosition="left">

              Ajouter un moyen de paiement
            </Button>
          </div>);


      case 'settings':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Paramètres du compte
              </h1>
              <p className="text-muted-foreground">
                Modifiez vos informations personnelles et préférences
              </p>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <AccountSettingsForm
                initialData={mockUserData}
                onSave={handleSaveSettings} />

            </div>

            <NotificationPreferences userEmail={mockUserData?.email} />
          </div>);


      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole="customer"
        userName={`${mockUserData?.firstName} ${mockUserData?.lastName}`}
        onLogout={handleLogout} />

      <div className="main-content">
        <div className="flex">
          <aside className={`
            fixed lg:sticky top-16 left-0 bottom-0 w-64 bg-card border-r border-border
            transform transition-transform duration-300 z-[900]
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}>
            <div className="h-full overflow-y-auto scrollbar-thin p-4">
              <nav className="space-y-1">
                {sidebarSections?.map((section) =>
                <button
                  key={section?.id}
                  onClick={() => {
                    setActiveSection(section?.id);
                    setIsSidebarOpen(false);
                  }}
                  className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                      transition-all duration-200
                      ${activeSection === section?.id ?
                  'bg-primary text-primary-foreground' :
                  'text-muted-foreground hover:text-foreground hover:bg-muted'}
                    `
                  }>

                    <Icon name={section?.icon} size={20} />
                    <span>{section?.label}</span>
                  </button>
                )}
              </nav>
            </div>
          </aside>

          {isSidebarOpen &&
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[890] lg:hidden"
            onClick={() => setIsSidebarOpen(false)} />

          }

          <main className="flex-1 min-w-0">
            <div className="lg:hidden sticky top-16 z-[800] bg-card border-b border-border px-4 py-3">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="flex items-center gap-2 text-sm font-medium text-foreground">

                <Icon name="Menu" size={20} />
                <span>Menu</span>
              </button>
            </div>

            <div className="p-4 lg:p-8 max-w-7xl mx-auto">
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
    </div>);

};

export default CustomerAccountPortal;